import { useEffect, useMemo, useRef, useState } from 'react';

const WEEK_DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEK_DAYS_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MODES = [
  { value: '2.00', label: '2×', color: '#3b82f6', light: '#dbeafe', dark: '#1d4ed8' },
  { value: '3.00', label: '3×', color: '#22c55e', light: '#dcfce7', dark: '#15803d' },
  { value: '4.00', label: '4×', color: '#f97316', light: '#ffedd5', dark: '#c2410c' },
  { value: '5.00', label: '5×', color: '#a855f7', light: '#f3e8ff', dark: '#7e22ce' },
  { value: '1.00', label: 'OFF', color: '#94a3b8', light: '#f1f5f9', dark: '#475569', isOff: true },
];

const getModeByValue = (v) =>
  MODES.find((m) => m.value === String(parseFloat(v).toFixed(2))) ?? MODES[0];

const toYMD = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const parseYMD = (ymd) => {
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(y, mo - 1, d);
};

const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const clamp = (a, b) => (cmp(a, b) <= 0 ? [a, b] : [b, a]);
const inRange = (date, s, e) => cmp(date, s) >= 0 && cmp(date, e) <= 0;
const overlapsRange = (s1, e1, s2, e2) => cmp(s1, e2) <= 0 && cmp(s2, e1) <= 0;

const addDays = (ymd, n) => {
  const d = parseYMD(ymd);
  d.setDate(d.getDate() + n);
  return toYMD(d.getFullYear(), d.getMonth(), d.getDate());
};

let _splitId = 0;
const splitAround = (range, newStart, newEnd) => {
  const pieces = [];
  if (cmp(range.start, newStart) < 0) {
    pieces.push({ ...range, id: `spl-${++_splitId}`, end: addDays(newStart, -1), saved: false });
  }
  if (cmp(range.end, newEnd) > 0) {
    pieces.push({ ...range, id: `spl-${++_splitId}`, start: addDays(newEnd, 1), saved: false });
  }
  return pieces;
};

const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const firstDOW = (y, m) => new Date(y, m, 1).getDay();

const TODAY = toYMD(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

const fmt = (ymd) => {
  if (!ymd) return '';
  return parseYMD(ymd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getCellProps = (ymd, allRanges, selStart, hoverDate, currentMode, isPast) => {
  const inSavedRange = allRanges.some((r) => inRange(ymd, r.start, r.end));
  const primaryRange = allRanges.find((r) => inRange(ymd, r.start, r.end));
  const primaryMode = primaryRange ? getModeByValue(primaryRange.multiplierValue) : null;

  let hasSel = false;
  if (selStart) {
    const pivot = hoverDate || selStart;
    const [s, e] = clamp(selStart, pivot);
    if (inRange(ymd, s, e)) { hasSel = true; }
  }

  const isSelStart = ymd === selStart;

  let bg = 'transparent';
  let color = isPast ? '#ccc' : '#111';

  if (inSavedRange && primaryMode) {
    bg = isSelStart ? primaryMode.color : primaryMode.light;
    color = isSelStart ? '#fff' : (isPast ? `${primaryMode.dark}66` : primaryMode.dark);
  }

  if (hasSel && !inSavedRange) {
    bg = isSelStart ? currentMode.color : `${currentMode.color}28`;
    color = isSelStart ? '#fff' : currentMode.dark;
  }

  if (hasSel && inSavedRange) {
    bg = isSelStart ? currentMode.color : `${currentMode.color}28`;
    color = isSelStart ? '#fff' : currentMode.dark;
  }

  const isToday = ymd === TODAY;
  const overlapCount = allRanges.filter((r) => inRange(ymd, r.start, r.end)).length;

  return { bg, color, isToday, overlapCount, primaryMode };
};

const RangeChip = ({ range, onRemove, readOnly }) => {
  const m = getModeByValue(range.multiplierValue);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '7px 11px', borderRadius: '8px',
      border: `1px solid ${m.color}40`, background: m.light,
    }}>
      <span style={{
        minWidth: '32px', textAlign: 'center', padding: '2px 6px',
        borderRadius: '4px', background: m.color, color: '#fff',
        fontWeight: 700, fontSize: '11px',
      }}>{m.label}</span>
      <span style={{ flex: 1, fontSize: '13px', color: m.dark }}>
        {fmt(range.start)}
        {range.start !== range.end && <> &ndash; {fmt(range.end)}</>}
      </span>
      {!range.saved && !readOnly && (
        <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>unsaved</span>
      )}
      {!readOnly && (
        <button
          type="button" onClick={onRemove}
          style={{
            width: '20px', height: '20px', border: 'none', borderRadius: '50%',
            background: `${m.color}25`, color: m.dark, cursor: 'pointer',
            fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >×</button>
      )}
    </div>
  );
};

const MiniMonth = ({ year, month, allRanges, selStart, hoverDate, currentMode, onDayClick, onDayHover, onMonthClick }) => {
  const days = daysInMonth(year, month);
  const startDOW = firstDOW(year, month);
  const cells = Array(startDOW).fill(null);
  for (let d = 1; d <= days; d++) cells.push(toYMD(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <button
        type="button"
        onClick={() => onMonthClick(year, month)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '11px', fontWeight: 700, color: '#333',
          padding: '0 0 5px 2px', letterSpacing: '0.2px',
          width: '100%', textAlign: 'left',
          borderBottom: '1px solid #f0f0f0', marginBottom: '4px',
        }}
      >
        {MONTH_SHORT[month]}
      </button>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        border: '1px solid #ebebeb', borderRadius: '4px', overflow: 'hidden',
      }}>
        {WEEK_DAYS_SHORT.map((d, i) => (
          <div key={i} style={{
            textAlign: 'center', fontSize: '8px', color: '#bbb',
            fontWeight: 600, padding: '2px 0',
            background: '#f8f9fa',
            borderRight: i < 6 ? '1px solid #ebebeb' : 'none',
            borderBottom: '1px solid #ebebeb',
          }}>{d}</div>
        ))}
        {cells.map((ymd, idx) => {
          const isLastCol = idx % 7 === 6;
          if (!ymd) return (
            <div key={`e-${idx}`} style={{
              height: '18px',
              background: '#fafafa',
              borderRight: isLastCol ? 'none' : '1px solid #ebebeb',
              borderBottom: '1px solid #ebebeb',
            }} />
          );
          const dayNum = parseInt(ymd.split('-')[2], 10);
          const isPast = cmp(ymd, TODAY) < 0;
          const { bg, color, isToday, primaryMode } = getCellProps(ymd, allRanges, selStart, hoverDate, currentMode, isPast);

          return (
            <div
              key={ymd}
              onClick={() => !isPast && onDayClick(ymd)}
              onMouseEnter={() => !isPast && onDayHover(ymd)}
              style={{
                height: '18px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '9px',
                fontWeight: isToday ? '700' : '400',
                cursor: isPast ? 'default' : 'pointer',
                background: bg || (isPast ? '#fdfdfd' : '#fff'),
                color,
                outline: isToday ? `1.5px solid ${primaryMode?.color ?? currentMode.color}` : 'none',
                outlineOffset: '-1px',
                borderRight: isLastCol ? 'none' : '1px solid #ebebeb',
                borderBottom: '1px solid #ebebeb',
                boxSizing: 'border-box',
              }}
            >
              {dayNum}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FullMonth = ({ year, month, allRanges, selStart, hoverDate, currentMode, onDayClick, onDayHover }) => {
  const days = daysInMonth(year, month);
  const startDOW = firstDOW(year, month);
  const cells = Array(startDOW).fill(null);
  for (let d = 1; d <= days; d++) cells.push(toYMD(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const CELL_BORDER = '1px solid #f0f0f0';

  return (
    <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WEEK_DAYS_FULL.map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', fontSize: '11px', color: '#aaa',
            fontWeight: 600, padding: '7px 0',
            letterSpacing: '0.3px',
            background: '#f8f9fa',
            borderRight: i < 6 ? CELL_BORDER : 'none',
            borderBottom: CELL_BORDER,
          }}>{d}</div>
        ))}
        {cells.map((ymd, idx) => {
          const isLastCol = idx % 7 === 6;
          if (!ymd) return (
            <div key={`e-${idx}`} style={{
              height: '44px',
              background: '#fafafa',
              borderRight: isLastCol ? 'none' : CELL_BORDER,
              borderBottom: CELL_BORDER,
            }} />
          );
          const dayNum = parseInt(ymd.split('-')[2], 10);
          const isPast = cmp(ymd, TODAY) < 0;
          const { bg, color, isToday, overlapCount, primaryMode } = getCellProps(ymd, allRanges, selStart, hoverDate, currentMode, isPast);

          return (
            <div
              key={ymd}
              onClick={() => !isPast && onDayClick(ymd)}
              onMouseEnter={() => !isPast && onDayHover(ymd)}
              style={{
                height: '44px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '13px',
                fontWeight: isToday ? '700' : '400',
                cursor: isPast ? 'default' : 'pointer',
                background: bg || (isPast ? '#fdfdfd' : '#fff'),
                color,
                outline: isToday ? `2px solid ${primaryMode?.color ?? currentMode.color}` : 'none',
                outlineOffset: '-2px',
                borderRight: isLastCol ? 'none' : CELL_BORDER,
                borderBottom: CELL_BORDER,
                transition: 'background 0.06s',
                position: 'relative',
                boxSizing: 'border-box',
              }}
            >
              {dayNum}
              {overlapCount > 1 && (
                <span style={{
                  position: 'absolute', top: '5px', right: '5px',
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: '#ef4444', color: '#fff',
                  fontSize: '7px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{overlapCount}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MultiplierScheduleCalendar = ({
  existingSchedules = [],
  onAddRange,
  onDeleteSchedule,
  saving = false,
  embeddedMode = false,
  onPendingRangesChange,
  readOnly = false,
}) => {
  const [view, setView] = useState('year');
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [editingYear, setEditingYear] = useState(false);
  const [yearInput, setYearInput] = useState('');
  const [selectedMode, setSelectedMode] = useState(MODES[0].value);
  const [selStart, setSelStart] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [pendingRanges, setPendingRanges] = useState([]);
  const [savedToDelete, setSavedToDelete] = useState([]);
  const [addError, setAddError] = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const yearInputRef = useRef(null);

  useEffect(() => {
    if (onPendingRangesChange) onPendingRangesChange(pendingRanges);
  }, [pendingRanges, onPendingRangesChange]);

  const currentMode = getModeByValue(selectedMode);

  const savedRanges = useMemo(() =>
    existingSchedules.map((s) => ({
      id: s.id,
      start: s.start_date,
      end: s.end_date,
      multiplierValue: String(parseFloat(s.multiplier_value).toFixed(2)),
      saved: true,
    })),
    [existingSchedules]
  );

  const allRanges = useMemo(() => [
    ...savedRanges.filter((r) => !savedToDelete.includes(r.id)),
    ...pendingRanges,
  ], [savedRanges, pendingRanges, savedToDelete]);

  const handleDayClick = (ymd) => {
    if (readOnly || cmp(ymd, TODAY) < 0) return;
    if (!selStart) { setSelStart(ymd); setAddError(''); return; }

    const [start, end] = clamp(selStart, ymd);

    const hitSaved = savedRanges.filter(
      (r) => !savedToDelete.includes(r.id) && overlapsRange(r.start, r.end, start, end)
    );
    const savedTrimmed = hitSaved.flatMap((r) => splitAround(r, start, end));
    const newToDelete = hitSaved.map((r) => r.id);

    const newRange = {
      id: `p-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      start, end, multiplierValue: currentMode.value, saved: false,
    };

    setPendingRanges((prev) => {
      const kept = [];
      for (const r of prev) {
        if (overlapsRange(r.start, r.end, start, end)) {
          kept.push(...splitAround(r, start, end));
        } else {
          kept.push(r);
        }
      }
      kept.push(newRange, ...savedTrimmed);
      return kept;
    });

    if (newToDelete.length) {
      setSavedToDelete((prev) => [...new Set([...prev, ...newToDelete])]);
    }

    setSelStart(null);
    setHoverDate(null);
  };

  const handleDayHover = (ymd) => { if (selStart) setHoverDate(ymd); };

  const handleMonthClick = (y, m) => {
    setViewYear(y);
    setViewMonth(m);
    setView('month');
  };

  const handleRemoveRange = (range) => {
    if (range.saved) onDeleteSchedule?.(range.id);
    else setPendingRanges((prev) => prev.filter((r) => r.id !== range.id));
  };

  const handleSaveAll = async () => {
    const hasPending = pendingRanges.length > 0;
    const hasDeletes = savedToDelete.length > 0;
    if (!hasPending && !hasDeletes) return;

    setSavingAll(true);
    setAddError('');

    for (const id of savedToDelete) {
      try { await onDeleteSchedule?.(id); } catch (err) { console.error('Failed to delete modified range:', err); }
    }

    for (const range of pendingRanges) {
      try {
        await onAddRange?.({ start_date: range.start, end_date: range.end, multiplier_value: range.multiplierValue });
      } catch (err) {
        const d = err?.response?.data;
        setAddError(d?.non_field_errors?.[0] || d?.start_date?.[0] || d?.end_date?.[0] || d?.multiplier_value?.[0] || 'Failed to save schedule.');
        setSavingAll(false);
        return;
      }
    }

    setPendingRanges([]);
    setSavedToDelete([]);
    setSavingAll(false);
  };

  const startEditYear = () => {
    setYearInput(String(viewYear));
    setEditingYear(true);
    setTimeout(() => yearInputRef.current?.select(), 0);
  };

  const commitYearEdit = () => {
    const parsed = parseInt(yearInput, 10);
    if (!isNaN(parsed) && parsed >= 2020 && parsed <= 2050) setViewYear(parsed);
    setEditingYear(false);
  };

  const prevPeriod = () => {
    if (view === 'year') {
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => { if (m === 0) { setViewYear((y) => y - 1); return 11; } return m - 1; });
    }
  };

  const nextPeriod = () => {
    if (view === 'year') {
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => { if (m === 11) { setViewYear((y) => y + 1); return 0; } return m + 1; });
    }
  };

  const hasPendingChanges = pendingRanges.length > 0 || savedToDelete.length > 0;

  const legend = MODES.map((m) => ({
    ...m, count: allRanges.filter((r) => r.multiplierValue === m.value).length,
  })).filter((m) => m.count > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

      {!readOnly && <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Select mode
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {MODES.map((m) => {
            const active = selectedMode === m.value;
            const count = allRanges.filter((r) => r.multiplierValue === m.value).length;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setSelectedMode(m.value)}
                style={{
                  padding: '6px 15px',
                  borderRadius: '100px',
                  border: `2px solid ${active ? m.color : '#e2e8f0'}`,
                  background: active ? m.color : '#fff',
                  color: active ? '#fff' : '#666',
                  fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}
              >
                {m.isOff && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5" stroke={active ? '#fff' : '#94a3b8'} strokeWidth="1.5" />
                    <line x1="3" y1="3" x2="9" y2="9" stroke={active ? '#fff' : '#94a3b8'} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                )}
                {m.label}
                {count > 0 && (
                  <span style={{
                    background: active ? 'rgba(255,255,255,0.28)' : `${m.color}22`,
                    color: active ? '#fff' : m.dark,
                    borderRadius: '100px', fontSize: '10px',
                    padding: '0 5px', fontWeight: 700, marginLeft: '2px',
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>}

      {!readOnly && selStart && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 14px', borderRadius: '8px',
          background: `${currentMode.color}0e`,
          border: `1px solid ${currentMode.color}40`,
          fontSize: '13px',
        }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: currentMode.color, flexShrink: 0,
            animation: 'calPulse 1.2s ease-in-out infinite',
          }} />
          <span style={{ color: currentMode.dark }}>
            Start: <strong>{fmt(selStart)}</strong> — click end date
          </span>
          <button type="button"
            onClick={() => { setSelStart(null); setHoverDate(null); }}
            style={{
              marginLeft: 'auto', padding: '3px 10px',
              border: '1px solid #d1d5db', borderRadius: '100px',
              background: '#fff', fontSize: '12px', cursor: 'pointer', color: '#666',
            }}>
            Cancel
          </button>
        </div>
      )}

      <div
        style={{ border: '1px solid #e9ecef', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}
        onMouseLeave={() => setHoverDate(null)}
      >
        <div style={{
          display: 'flex', alignItems: 'center',
          borderBottom: '1px solid #f0f0f0', background: '#fafafa',
        }}>
          <div style={{ display: 'flex', borderRight: '1px solid #f0f0f0' }}>
            {['year', 'month'].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setView(tab)}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  background: view === tab ? '#fff' : 'transparent',
                  borderBottom: view === tab ? '2px solid #3b82f6' : '2px solid transparent',
                  color: view === tab ? '#1d4ed8' : '#888',
                  fontWeight: view === tab ? 700 : 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                  letterSpacing: '0.2px',
                  transition: 'all 0.12s',
                }}
              >
                {tab === 'year' ? 'Year' : 'Month'}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 16px' }}>
            <button type="button" onClick={prevPeriod} style={navBtn}>‹</button>

            {view === 'year' ? (
              editingYear ? (
                <input
                  ref={yearInputRef}
                  value={yearInput}
                  onChange={(e) => setYearInput(e.target.value)}
                  onBlur={commitYearEdit}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitYearEdit(); if (e.key === 'Escape') setEditingYear(false); }}
                  style={{
                    width: '68px', textAlign: 'center', fontSize: '15px',
                    fontWeight: 700, border: '2px solid #3b82f6', borderRadius: '6px',
                    padding: '2px 6px', outline: 'none',
                  }}
                />
              ) : (
                <button type="button" onClick={startEditYear} style={{
                  fontSize: '15px', fontWeight: 700, color: '#111',
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '2px 8px', borderRadius: '6px',
                  borderBottom: '2px dashed #d1d5db',
                }} title="Click to jump to a year">{viewYear}</button>
              )
            ) : (
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#111', minWidth: '160px', textAlign: 'center' }}>
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
            )}

            <button type="button" onClick={nextPeriod} style={navBtn}>›</button>
          </div>

          <div style={{ width: '120px' }} />
        </div>

        {view === 'year' ? (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1px', background: '#f0f0f0',
          }}>
            {Array.from({ length: 12 }, (_, m) => (
              <div key={m} style={{ background: '#fff', padding: '14px 12px 12px' }}>
                <MiniMonth
                  year={viewYear}
                  month={m}
                  allRanges={allRanges}
                  selStart={selStart}
                  hoverDate={hoverDate}
                  currentMode={currentMode}
                  onDayClick={handleDayClick}
                  onDayHover={handleDayHover}
                  onMonthClick={handleMonthClick}
                />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '12px 14px 14px' }}>
            <FullMonth
              year={viewYear}
              month={viewMonth}
              allRanges={allRanges}
              selStart={selStart}
              hoverDate={hoverDate}
              currentMode={currentMode}
              onDayClick={handleDayClick}
              onDayHover={handleDayHover}
            />
          </div>
        )}

        {legend.length > 0 && (
          <div style={{
            padding: '8px 16px', borderTop: '1px solid #f0f0f0',
            display: 'flex', gap: '14px', flexWrap: 'wrap',
            background: '#fafafa',
          }}>
            {legend.map((m) => (
              <div key={m.value} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: m.dark }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: m.color, flexShrink: 0 }} />
                {m.label} · {m.count} range{m.count !== 1 ? 's' : ''}
              </div>
            ))}
          </div>
        )}
      </div>

      {addError && (
        <div style={{
          padding: '10px 14px', borderRadius: '8px',
          background: '#fff5f5', border: '1px solid #fecaca',
          color: '#b00020', fontSize: '13px',
        }}>{addError}</div>
      )}

      {allRanges.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Scheduled ranges
          </div>
          {allRanges.map((range) => (
            <RangeChip key={range.id} range={range} onRemove={() => handleRemoveRange(range)} readOnly={readOnly} />
          ))}
        </div>
      )}

      {!readOnly && hasPendingChanges && !embeddedMode && (
        <button
          type="button"
          className="staff-page-button"
          onClick={handleSaveAll}
          disabled={savingAll || saving}
          style={{ alignSelf: 'flex-start' }}
        >
          <span className="staff-page-button-text">
            {savingAll
              ? 'Saving…'
              : pendingRanges.length > 0
                ? `Save ${pendingRanges.length} range${pendingRanges.length !== 1 ? 's' : ''}${savedToDelete.length > 0 ? ` · remove ${savedToDelete.length} modified` : ''}`
                : `Remove ${savedToDelete.length} modified range${savedToDelete.length !== 1 ? 's' : ''}`}
          </span>
        </button>
      )}

      {!readOnly && pendingRanges.length > 0 && embeddedMode && (
        <div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
          {pendingRanges.length} range{pendingRanges.length !== 1 ? 's' : ''} will be saved with the multiplier.
        </div>
      )}

      <style>{`
        @keyframes calPulse {
          0%,100% { opacity:1; transform:scale(1); }
          50% { opacity:0.35; transform:scale(0.7); }
        }
      `}</style>
    </div>
  );
};

const navBtn = {
  width: '28px', height: '28px',
  border: '1px solid #e2e8f0', borderRadius: '6px',
  background: '#fff', cursor: 'pointer',
  fontSize: '16px', color: '#555',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default MultiplierScheduleCalendar;
